import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { Section, Spinner } from "../components/Bits";
import { ChevronLeft, Clock } from "lucide-react";

// Lightweight markdown renderer
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const blocks = [];
  let listBuf = [];
  let tableBuf = [];
  const flushList = (k) => {
    if (listBuf.length) {
      blocks.push(
        <ul key={`ul-${k}`} className="list-disc pl-6 space-y-1 my-3">
          {listBuf.map((l, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(l) }} />)}
        </ul>
      );
      listBuf = [];
    }
  };
  const flushTable = (k) => {
    if (tableBuf.length) {
      const rows = tableBuf.filter((l) => !/^\|?\s*[-:|\s]+\|/.test(l));
      const parsed = rows.map((l) => l.split("|").map((c) => c.trim()).filter(Boolean));
      const [head, ...body] = parsed;
      blocks.push(
        <div key={`tab-${k}`} className="overflow-x-auto my-4">
          <table className="w-full text-sm border-collapse">
            <thead><tr>{head.map((h, i) => <th key={i} className="text-left p-2 border-b font-semibold" style={{ borderColor: "var(--wp-border)" }}>{h}</th>)}</tr></thead>
            <tbody>{body.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="p-2 border-b" style={{ borderColor: "var(--wp-border)" }} dangerouslySetInnerHTML={{ __html: inline(c) }} />)}</tr>)}</tbody>
          </table>
        </div>
      );
      tableBuf = [];
    }
  };
  const inline = (s) => s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="underline" target="_blank" rel="noopener">$1</a>')
    .replace(/`(.+?)`/g, '<code class="px-1 rounded bg-black/5">$1</code>');

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (line.startsWith("|") && line.endsWith("|")) {
      flushList(idx); tableBuf.push(line); return;
    } else if (tableBuf.length) flushTable(idx);

    if (/^###\s+/.test(line)) { flushList(idx); blocks.push(<h3 key={idx} className="font-display text-2xl mt-6 mb-2">{line.replace(/^###\s+/, "")}</h3>); }
    else if (/^##\s+/.test(line)) { flushList(idx); blocks.push(<h2 key={idx} className="font-display text-3xl mt-8 mb-3">{line.replace(/^##\s+/, "")}</h2>); }
    else if (/^#\s+/.test(line)) { flushList(idx); blocks.push(<h1 key={idx} className="font-display text-4xl mt-8 mb-4">{line.replace(/^#\s+/, "")}</h1>); }
    else if (/^>\s+/.test(line)) { flushList(idx); blocks.push(<blockquote key={idx} className="my-4 pl-4 border-l-4 italic opacity-80" style={{ borderColor: "var(--wp-primary)" }} dangerouslySetInnerHTML={{ __html: inline(line.replace(/^>\s+/, "")) }} />); }
    else if (/^---/.test(line)) { flushList(idx); blocks.push(<hr key={idx} className="my-6" style={{ borderColor: "var(--wp-border)" }} />); }
    else if (/^[-*]\s+/.test(line)) { listBuf.push(line.replace(/^[-*]\s+/, "")); }
    else if (/^\d+\.\s+/.test(line)) { listBuf.push(line.replace(/^\d+\.\s+/, "")); }
    else if (line.trim() === "") { flushList(idx); }
    else { flushList(idx); blocks.push(<p key={idx} className="my-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(line) }} />); }
  });
  flushList("end"); flushTable("end");
  return blocks;
}

const CATEGORY_LABELS = { wissen: "Wissen", rezept: "Rezepte", saison: "Saison", sicherheit: "Sicherheit", luxemburg: "Luxemburg" };

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <Section><Spinner /></Section>;
  if (!article) return <Section><div className="text-center opacity-70">Artikel nicht gefunden.</div></Section>;

  return (
    <Section>
      <Link to="/magazin" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-6" data-testid="article-back">
        <ChevronLeft className="w-4 h-4" /> Zurück zum Magazin
      </Link>

      <div className="max-w-3xl mx-auto" data-testid={`article-${article.slug}`}>
        {article.cover_image && (
          <img src={article.cover_image} alt={(article.cover_image || "").startsWith("/fotos/") && article.cover_alt ? article.cover_alt : article.title} className="w-full aspect-[16/9] object-cover rounded-2xl mb-8" />
        )}

        <div className="flex items-center gap-3 mb-3 text-xs">
          <span className="px-2 py-1 rounded-full" style={{ backgroundColor: "rgba(45,90,39,0.12)" }}>
            {CATEGORY_LABELS[article.category] || article.category}
          </span>
          <span className="flex items-center gap-1 opacity-60"><Clock className="w-3 h-3" /> {new Date(article.created_at).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" })}</span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl mb-4 leading-tight">{article.title}</h1>
        <p className="text-lg opacity-80 mb-2">{article.excerpt}</p>
        <p className="text-xs opacity-60 mb-8">von {article.author || "AdventureCouple.lu"}</p>

        <div className="article-body text-[var(--wp-text)] leading-relaxed" data-testid="article-content">
          {renderMarkdown(article.body)}
        </div>

        <div className="mt-12 pt-8 border-t" style={{ borderColor: "var(--wp-border)" }}>
          <p className="text-sm opacity-70 mb-4">Hat dir der Artikel gefallen?</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/magazin" className="btn-secondary">Weitere Artikel</Link>
            <Link to="/tours" className="btn-primary">An einer Tour teilnehmen</Link>
          </div>
        </div>
      </div>
    </Section>
  );
}
